import React, { useMemo, useState } from "react";
import { useCallback } from "react";
import { useMemoRequest } from "../../../../hooks/useMemoRequest";
import VerticalGroup from "../../../atoms/verticalgroup/VerticalGroup";
import ModelAPI from "../../../../services/modelApi";

import ModelPageContainer from "../ModelPageContainer";
import getTeamIdsAsFields from "../utils/getTeamIdsAsFields";
import getTeamName from "../utils/getTeamName";
import ModelAdjustmentsSection from "./ModelAdjustmentSection";
import modelApi from "../../../../services/modelApi";
import { percentage } from "../utils/formatters";

interface Props {
  eventId: string;
  configuration: ModelAPI.ConfigurationResponse;
  selectedTeam: number;
  selectedRound: number;
  teams: API.GameConnection[];
}

const ModelAdjustments: React.FC<Props> = ({
  eventId,
  selectedRound,
  configuration,
  teams,
}) => {
  const [updateError, setUpdateError] = useState<Error | null>(null);
  const { numberOfTeams } = configuration;

  const getAdjustments = useCallback(() => {
    return ModelAPI.getAdjustments(eventId);
  }, [eventId]);

  const { inProgress, data, error, refresh } =
    useMemoRequest<ModelAPI.FacilitatorAdjustmentsResponse>(getAdjustments);

  const getResults = useCallback(() => {
    return ModelAPI.getModelTeamResultsAll(eventId, selectedRound);
  }, [eventId, selectedRound]);

  const {
    inProgress: resultInProgress,
    data: resultsData,
    error: resultsError,
    refresh: resultsRefresh,
  } = useMemoRequest<ModelAPI.RoundResultsResponse[]>(getResults);

  const fields = useMemo(
    () => getTeamIdsAsFields(numberOfTeams),
    [numberOfTeams],
  );

  const onUpdate = useCallback(
    async (
      type: keyof ModelAPI.FacilitatorAdjustmentsResponse,
      teamId: number,
      year: number,
      value: number,
    ) => {
      try {
        setUpdateError(null);
        await modelApi.updateAdjustments(
          eventId,
          year - configuration.numberOfHistoricRounds + 1,
          {
            teamId,
            type,
            value,
          },
        );
      } catch (e) {
        setUpdateError(e);
      }
    },
    [configuration.numberOfHistoricRounds, eventId],
  );

  const teamNameFieldFormatter = useCallback(
    (field: string) => getTeamName(parseInt(field), numberOfTeams, teams),
    [numberOfTeams, teams],
  );
  const percentageFormatter = useCallback(
    (value: any) => percentage(value),
    [],
  );

  return (
    <ModelPageContainer
      eventId={eventId}
      selectedRound={selectedRound}
      data={data}
      refresh={() => {
        refresh();
        resultsRefresh();
      }}
      error={updateError ?? error ?? resultsError}
      inProgress={inProgress || resultInProgress}
    >
      {!!data && !!resultsData && (
        <VerticalGroup spaceBetweenElements={4} wide>
          <ModelAdjustmentsSection
            title={"NPS"}
            configuration={configuration}
            numberOfTeams={numberOfTeams}
            adjustmentData={data}
            adjustmentField={"nps"}
            resultData={resultsData}
            resultField={"nps"}
            fields={fields}
            fieldFormatter={teamNameFieldFormatter}
            onUpdate={onUpdate}
          />
          <ModelAdjustmentsSection
            title={"Total Shareholder Return"}
            configuration={configuration}
            numberOfTeams={numberOfTeams}
            adjustmentData={data}
            adjustmentField={"tsr"}
            resultData={resultsData}
            resultField={"shareholderReturns"}
            fields={fields}
            fieldFormatter={teamNameFieldFormatter}
            formatter={percentageFormatter}
            onUpdate={onUpdate}
          />
          <ModelAdjustmentsSection
            title={"Employee Engagement"}
            configuration={configuration}
            numberOfTeams={numberOfTeams}
            adjustmentData={data}
            adjustmentField={"employeeEngagement"}
            resultData={resultsData}
            resultField={"employeeEngagement"}
            fields={fields}
            fieldFormatter={teamNameFieldFormatter}
            formatter={percentageFormatter}
            onUpdate={onUpdate}
          />
          <ModelAdjustmentsSection
            title={"Complaints"}
            configuration={configuration}
            numberOfTeams={numberOfTeams}
            adjustmentData={data}
            adjustmentField={"complaints"}
            resultData={resultsData}
            resultField={"complaints"}
            fields={fields}
            fieldFormatter={teamNameFieldFormatter}
            onUpdate={onUpdate}
          />
          <ModelAdjustmentsSection
            title={"Risk Incidents"}
            configuration={configuration}
            numberOfTeams={numberOfTeams}
            adjustmentData={data}
            adjustmentField={"riskIncidents"}
            resultData={resultsData}
            resultField={"riskIncidents"}
            fields={fields}
            fieldFormatter={teamNameFieldFormatter}
            onUpdate={onUpdate}
          />
          <ModelAdjustmentsSection
            title={"Capital Ratio"}
            configuration={configuration}
            numberOfTeams={numberOfTeams}
            adjustmentData={data}
            adjustmentField={"capitalRatio"}
            resultData={resultsData}
            resultField={"capitalRatio"}
            fields={fields}
            fieldFormatter={teamNameFieldFormatter}
            formatter={percentageFormatter}
            onUpdate={onUpdate}
          />
          <ModelAdjustmentsSection
            title={"Loan Impairment Expenses"}
            configuration={configuration}
            numberOfTeams={numberOfTeams}
            adjustmentData={data}
            adjustmentField={"lie"}
            resultData={resultsData}
            resultField={"lie"}
            fields={fields}
            fieldFormatter={teamNameFieldFormatter}
            formatter={percentageFormatter}
            onUpdate={onUpdate}
          />
          <ModelAdjustmentsSection
            title={"Regulator Actions"}
            configuration={configuration}
            numberOfTeams={numberOfTeams}
            adjustmentData={data}
            adjustmentField={"regulatorActions"}
            resultData={resultsData}
            resultField={"regulatorActions"}
            fields={fields}
            fieldFormatter={teamNameFieldFormatter}
            onUpdate={onUpdate}
          />
          <ModelAdjustmentsSection
            title={"Community Trust"}
            configuration={configuration}
            numberOfTeams={numberOfTeams}
            adjustmentData={data}
            adjustmentField={"communityTrust"}
            resultData={resultsData}
            resultField={"communityTrust"}
            fields={fields}
            fieldFormatter={teamNameFieldFormatter}
            formatter={percentageFormatter}
            onUpdate={onUpdate}
          />
        </VerticalGroup>
      )}
    </ModelPageContainer>
  );
};

export default ModelAdjustments;
